import Head from 'next/head';
import { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { FunctionComponent, useEffect } from 'react';

import useLoggedUser from '../../../../../hooks/use-logged-user';
import usePageViewEvent from '../../../../../hooks/use-page-view-event';
import useHreflang from '../../../../../utils/seo/hreflang';
import useOGMeta from '../../../../../utils/seo/ogmeta';
import useSchema from '../../../../../utils/seo/schema-org/schema';
import { useBreadcrumbsType } from '../../../vehicle/hooks/use-breadcrumbs';

const ModelsSchema: FunctionComponent<{
  selection: any;
  info: any;
  vehicle: any;
  breadcrumbs: ReturnType<useBreadcrumbsType>;
  translatedSlugs: any;
  stock: TResultAPIStocksItem[];
}> = ({ selection, info, vehicle, breadcrumbs, translatedSlugs, stock }) => {
  const { locale = 'en', asPath, pathname, query } = useRouter();
  const localePath = locale !== 'en' ? `/${locale}` : '';
  const { t } = useTranslation();
  const user = useLoggedUser();
  const subCategoryPlural = `parts-and-categories-plural:parts.${selection?.category.category}.${info?.subCategoryEnPart}`;

  const name = selection
    ? t('models:head.with-part.title', {
      part: info?.subCategoryEnPart
        ? t(
          subCategoryPlural,
          {},
          {
            fallback: info?.subCategory,
          }
        )
        : info?.subCategory,
      brand: vehicle.brand.name,
      count: stock && stock[0]?.stock.toLocaleString(locale) || 0,
      usedWord:
        info?.subCategoryType && info?.subCategoryType === 'F'
          ? t('grammar:words.used.feminine')
          : t('grammar:words.used.masculine'),
    })
    : t('models:head.no-part.title', {
      brand: vehicle.brand.name,
      count: stock && stock[0]?.stock.toLocaleString(locale) || 0,
    });

  const description = selection
    ? t('models:head.with-part.description', {
      part: info?.subCategoryEnPart
        ? t(
          subCategoryPlural,
          {},
          {
            fallback: info?.subCategory,
          }
        )
        : info?.subCategory,
      brand: vehicle.brand.name,
      count: stock && stock[0]?.stock.toLocaleString(locale) || 0,
      yoursWord:
        info?.subCategoryType && info?.subCategoryType === 'F'
          ? t('grammar:pronnouns.yours.feminine')
          : t('grammar:pronnouns.yours.masculine'),
      usedWord:
        info?.subCategoryType && info?.subCategoryType === 'F'
          ? t('grammar:words.used.feminine')
          : t('grammar:words.used.masculine'),
    })
    : t('models:head.no-part.description', {
      brand: vehicle.brand.name,
      count: stock && stock[0]?.stock.toLocaleString(locale),
    });

  const og = useOGMeta({
    title: name,
    description,
    url: process.env.NEXT_PUBLIC_HOST + localePath + asPath,
    image: vehicle.brand.logo_url,
  });

  const alternateLinks = useHreflang({
    locale,
    route: pathname.includes('[subCategory]') ? 'models-with-part' : 'models',
    params: query,
    translatedSlugs: translatedSlugs?.slugs,
  });

  usePageViewEvent(name);

  return (
    <Head>
      <title>{name}</title>
      <meta name="description" key="description" content={description} />
      {alternateLinks}
      {og}

      <script {...useSchema({ name, description, breadcrumbs })} />
    </Head>
  );
};

export default ModelsSchema;
